/**
 * Tableau de bord : assemble les blocs. Démo locale ou API live.
 */
import { useEffect, useState } from "react";
import { facturxFetch } from "../api/client";
import { PageHeader } from "../components/PageHeader";
import { ActivityFeed } from "../features/dashboard/ActivityFeed";
import { BillingOutlook } from "../features/dashboard/BillingOutlook";
import { DASH_DEMO } from "../features/dashboard/demo";
import { persistDashDemo, readDashDemo } from "../features/dashboard/demoMode";
import { DemoToggle } from "../features/dashboard/DemoToggle";
import { EncaissementCompare } from "../features/dashboard/EncaissementCompare";
import "../features/dashboard/dashboard.css";
import { KpiRow } from "../features/dashboard/KpiRow";
import { RealizedPanel } from "../features/dashboard/RealizedPanel";
import { MailDays } from "../features/dashboard/MailDays";
import { QueueList } from "../features/dashboard/QueueList";
import { EMPTY_KPIS, type DashKpi, type DashPayload, type DashTarget } from "../features/dashboard/types";

function openKpi(kpi: DashKpi): DashTarget {
  const fallback =
    kpi.id === "overdue" ? "overdue" : kpi.id === "upcoming" ? "upcoming" : kpi.id === "quotes" ? "relance" : kpi.id === "paid_month" ? "month" : undefined;
  const page = kpi.open?.page ?? (kpi.id === "paid_month" ? "payments" : kpi.id === "quotes" ? "quotes" : "invoices");
  return { page, id: kpi.open?.id, filter: kpi.open?.filter || fallback };
}

type DashboardPageProps = {
  companyId: number;
  onOpen: (target: DashTarget) => void;
};

export function DashboardPage({ companyId, onOpen }: DashboardPageProps) {
  const [demo, setDemo] = useState(readDashDemo);
  const [live, setLive] = useState<DashPayload | null>(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(!readDashDemo());

  useEffect(() => {
    if (demo) {
      setLive(null);
      setErr("");
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    facturxFetch<DashPayload>(`dashboard.php?company_id=${companyId}`)
      .then((data) => {
        if (!cancelled) {
          setLive(data);
          setErr("");
        }
      })
      .catch((e) => {
        if (!cancelled) {
          setLive(null);
          setErr(e instanceof Error ? e.message : "Impossible de charger le tableau de bord.");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [demo, companyId]);

  const data = demo ? DASH_DEMO : live;

  return (
    <div className="fx-dash">
      <PageHeader
        title="Tableau de bord"
        action={<DemoToggle on={demo} onChange={(on) => { persistDashDemo(on); setDemo(on); }} />}
      />
      {demo ? <p className="fx-demo-banner">Données fictives — rien n’est écrit en base.</p> : null}
      {!demo && loading ? <p className="fx-demo-banner">Chargement des chiffres…</p> : null}
      {!demo && err ? <p className="fx-dash-err">{err}</p> : null}
      <KpiRow
        items={data?.kpis ?? EMPTY_KPIS}
        onOpen={(kpi) => onOpen(openKpi(kpi))}
      />
      <BillingOutlook billing={data?.billing} />
      <RealizedPanel realized={data?.realized} />
      <EncaissementCompare demo={demo} companyId={companyId} />
      <MailDays days={data?.mail_days} />
      <div className="fx-queue-grid">
        <QueueList
          title="Factures en retard"
          empty="Aucune facture en retard pour l’instant."
          items={data?.overdue}
          onMore={() => onOpen({ page: "invoices", filter: "overdue" })}
          onOpen={(row) => row.open && onOpen(row.open)}
        />
        <QueueList
          title="Factures à venir"
          empty="Les factures envoyées, pas encore échues, s’afficheront ici."
          items={data?.upcoming}
          onMore={() => onOpen({ page: "invoices", filter: "upcoming" })}
          onOpen={(row) => row.open && onOpen(row.open)}
        />
        <QueueList
          title="Devis expirés"
          empty="Les devis hors validité s’afficheront ici."
          items={data?.quotes}
          onMore={() => onOpen({ page: "quotes", filter: "expired" })}
          onOpen={(row) => row.open && onOpen(row.open)}
        />
        <QueueList
          title="Récurrentes à venir"
          empty="Les prochaines générations s’afficheront ici."
          items={data?.recurring}
          onMore={() => onOpen({ page: "recurring", filter: "soon" })}
          onOpen={(row) => row.open && onOpen(row.open)}
        />
      </div>
      <ActivityFeed items={data?.activity} onOpen={(row) => row.open && onOpen(row.open)} />
    </div>
  );
}
